/*  ===========================================================
    src/Pages/ProjectDetails.js
    -----------------------------------------------------------
    + Shows Project Type (Album · EP · Single · Multi-Single)
    + Edit now uses shared ProjectModal (scrollable, DRY)
    ======================================================== */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../api';

import ReleaseModal from '../Components/ReleaseModal';
import ProjectModal from '../Components/ProjectModal';

import trashIcon   from '../assets/icons/trash.svg';
import pencilIcon  from '../assets/icons/pencil.svg';
import archiveIcon from '../assets/icons/archive.svg';
import emptyArt    from '../assets/emptyart.jpg';

import ProjectInvoices    from '../Components/ProjectInvoices';
import MarketingBreakdown from '../Components/MarketingBreakdown';

/* ─── helpers ────────────────────────────────────────────── */
const costsColorMap = {
  Recoupable:                   '#9eeb68',
  'Recoupable (-Side Artists)': 'orange',
  'Non-Recoupable':             '#ff4c4c',
};

const projectTypeColors = {
  Album:        '#31576c',
  EP:           '#583e72',
  Single:       '#3a3a3a',
  'Multi-Single':'3a3a3a',
};

/* ─── component ──────────────────────────────────────────── */
function ProjectDetails({ handleInvoiceStatusChange }) {
  const { id } = useParams();
  const navigate = useNavigate();

  /* 1 — data */
  const [project,       setProject]       = useState(null);
  const [allReleases,   setAllReleases]   = useState([]);
  const [allArtists,    setAllArtists]    = useState([]);
  const [allProjects,   setAllProjects]   = useState([]);
  const [allInvoices,   setAllInvoices]   = useState([]);

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

const fetchReleases = async (projectId) => {
  try {
    const res = await api(`/api/releases/paged?projectId=${encodeURIComponent(projectId)}&limit=200&page=1`);
    if (!res.ok) throw new Error(`Fetch releases failed (${res.status})`);
    const { items = [] } = await res.json();
    setAllReleases(items);
  } catch (err) {
    console.error(err);
  }
};

  /* 2 — ui state */
  const [isEditModalOpen,     setIsEditModalOpen]     = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isArchiveConfirmOpen,setIsArchiveConfirmOpen]= useState(false);
  const [showReleaseModal,    setShowReleaseModal]    = useState(false);
  const [showReactiveBudgetModal, setShowReactiveBudgetModal] = useState(false);
  const [editingReactiveBudget, setEditingReactiveBudget] = useState(null);
  const [reactiveBudgetAmount, setReactiveBudgetAmount] = useState('');
  const [reactiveBudgetNote, setReactiveBudgetNote] = useState('');
  const [budgetBreakdownOpen, setBudgetBreakdownOpen] = useState(false);

  /* 3 — tab */
  const [activeTab, setActiveTab] = useState('marketing');

  /* 3.5 — populate reactive budget form when editing */
  useEffect(() => {
    if (editingReactiveBudget) {
      setReactiveBudgetAmount(String(editingReactiveBudget.amount || ''));
      setReactiveBudgetNote(editingReactiveBudget.note || '');
    } else {
      setReactiveBudgetAmount('');
      setReactiveBudgetNote('');
    }
  }, [editingReactiveBudget]);

  /* 4 — fetch */
  useEffect(()=>{
    if(!id) return;

    (async()=>{
      try{
        setLoading(true); setError(null);
        const res = await api(`/api/projects/${id}`);
        if(!res.ok) throw new Error(`Fetch project failed (${res.status})`);
        setProject(await res.json());
      }catch(err){ setError(err.message); }finally{ setLoading(false); }
    })();

    fetchReleases(id);
    api('/api/artists').then(r=>r.json()).then(setAllArtists).catch(console.error);
    api('/api/projects/tiny').then(r=>r.json()).then(setAllProjects).catch(console.error);
    api(`/api/invoices?projectId=${id}`)
      .then(r=>r.json()).then(setAllInvoices).catch(console.error);

  },[id]);

  /* 5 — early returns */
  if(loading)  return <p style={{color:'#fff',padding:20}}>Loading…</p>;
  if(error)    return <p style={{color:'#fff',padding:20}}>Error: {error}</p>;
  if(!project) return <p style={{color:'#fff',padding:20}}>Project not found</p>;

  /* helpers */
  const dateOpts={day:'2-digit',month:'2-digit',year:'2-digit'};
  const relDate = project.releaseDate
        ? new Date(project.releaseDate).toLocaleDateString('en-GB',dateOpts).replaceAll('/','.')
        : '';
  const displayedArtists = Array.isArray(project.artists)
        ? project.artists.map(a=>a.name) : [];

  // Calculate total budget (initial + reactive budgets)
  const initialBudget = Number(project.budget) || 0;
  const reactiveBudgetsTotal = (project.reactiveBudgets || []).reduce((sum, b) => sum + (Number(b.amount) || 0), 0);
  const totalBudget = initialBudget + reactiveBudgetsTotal;

  /* releases linked to project */
  const linkedReleases = allReleases
    .filter(r=>String(r.projectId)===String(project._id))
    .sort((a,b)=>{
      if(!a.releaseDate&&!b.releaseDate) return 0;
      if(!a.releaseDate) return 1;
      if(!b.releaseDate) return -1;
      return new Date(a.releaseDate)-new Date(b.releaseDate);
    });

  /* Build initial data for edit modal
     ⬇️ Include NEW fields so the modal shows/saves them */
  const initialForEdit = {
    _id:          project._id, // IMPORTANT: include _id so contract upload can find the project
    title:        project.title || '',
    projectType:  project.projectType || '',
    artists:      Array.isArray(project.artists) ? project.artists.map(a => a._id) : [],
    releaseDate:  project.releaseDate ? project.releaseDate.slice(0,10) : '',
    endDate:      project.endDate ? project.endDate.slice(0,10) : '',
    coverArt:     project.coverArt || '',
    budget:       project.budget ?? '',
    costs:        project.costs || '',
    isArchived:   !!project.isArchived,

    // NEW
    labelShare:   project.labelShare ?? '',
    termYears:    project.termYears  ?? '',
    syncRights:   project.syncRights ?? '',
    territory:    project.territory  ?? '',
    contract:     project.contract || '', // include contract field
    contracts:    Array.isArray(project.contracts) ? project.contracts : [],
  };

  /* Reactive Budget Handlers */
  const handleAddReactiveBudget = async () => {
    if (!reactiveBudgetAmount || isNaN(reactiveBudgetAmount)) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const res = await api(`/api/reactive-budgets/${project._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(reactiveBudgetAmount),
          note: reactiveBudgetNote,
        }),
      });

      if (!res.ok) throw new Error('Failed to add reactive budget');
      const updated = await res.json();
      setProject(updated);
      setShowReactiveBudgetModal(false);
      setReactiveBudgetAmount('');
      setReactiveBudgetNote('');
      setEditingReactiveBudget(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateReactiveBudget = async () => {
    if (!editingReactiveBudget || !reactiveBudgetAmount || isNaN(reactiveBudgetAmount)) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const res = await api(`/api/reactive-budgets/${project._id}/${editingReactiveBudget._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: Number(reactiveBudgetAmount),
          note: reactiveBudgetNote,
        }),
      });

      if (!res.ok) throw new Error('Failed to update reactive budget');
      const updated = await res.json();
      setProject(updated);
      setShowReactiveBudgetModal(false);
      setReactiveBudgetAmount('');
      setReactiveBudgetNote('');
      setEditingReactiveBudget(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteReactiveBudget = async (budgetId) => {
    if (!window.confirm('Delete this reactive budget?')) return;

    try {
      const res = await api(`/api/reactive-budgets/${project._id}/${budgetId}`, {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error('Failed to delete reactive budget');
      const updated = await res.json();
      setProject(updated);
    } catch (err) {
      alert(err.message);
    }
  };

  /* Update handler used by ProjectModal */
  const handleUpdateProject = async (payload) => {
    try {
      // If payload is a full project object (has _id and many fields), just update state
      // This happens when refreshing after contract upload
      if (payload._id && Object.keys(payload).length > 10) {
        setProject(payload);
        setIsEditModalOpen(false);
        return;
      }

      // Otherwise, it's a normal update with changed fields only
      const res = await api(`/api/projects/${project._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
      const updated = await res.json();
      setProject(updated);
      setIsEditModalOpen(false);
    } catch (err) {
      alert(err.message);
    }
  };

  /* ─────────────────────────────── JSX ───────────────────────────── */
  return (
    <div style={{padding:20,color:'#fff'}}>

      {/* BACK */}
      <button onClick={()=>navigate('/project-overview')} style={{
        background:'transparent',border:'none',color:'#fff',fontSize:24,cursor:'pointer',
        marginBottom:20,display:'flex',alignItems:'center'}}>
        <svg width="32" height="32" viewBox="0 0 24 24" style={{marginRight:8,fill:'#333'}}>
          <path d="M10 6 L8.59 7.41 13.17 12 l-4.58 4.59L10 18 l6-6z" transform="rotate(180,12,12)"/>
        </svg>
      </button>

      {/* HEADER */}
      <div style={{display:'flex',alignItems:'flex-start',marginBottom:20}}>
        <img
          src={project.coverArt || emptyArt}
          alt=""
          style={{ width:300, height:300, objectFit:'cover', borderRadius:10, marginRight:20 }}
        />

        <div style={{flex:1}}>
          <div style={{display:'flex',alignItems:'center',marginBottom:10, gap: 10}}>
            <h1 style={{margin:0}}>{project.title}</h1>

            {/* edit */}
            <svg
              onClick={() => setIsEditModalOpen(true)}
              width="20" height="20" style={{cursor:'pointer',fill:'#333'}}
              viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM21 7.04a1.2 1.2 0 0 0 0-1.68l-2.34-2.34a1.2 1.2 0 0 0-1.68 0l-1.84 1.84 3.75 3.75L21 7.04z"/>
            </svg>

            {/* Contract */}
            <svg
              onClick={() => navigate(`/contract/${project._id}`)}
              width="20"
              height="20"
              style={{
                cursor: 'pointer',
                fill: (project.contract || (Array.isArray(project.contracts) && project.contracts.length)) ? '#333' : '#666',
                opacity: (project.contract || (Array.isArray(project.contracts) && project.contracts.length)) ? 1 : 0.5,
              }}
              viewBox="0 0 24 24"
              title={(project.contract || (Array.isArray(project.contracts) && project.contracts.length)) ? 'View Contract' : 'No contract uploaded'}
            >
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>

            {/* archive */}
            <img
              src={archiveIcon}
              alt="arc"
              onClick={()=>setIsArchiveConfirmOpen(true)}
              style={{width:20,height:20,cursor:'pointer',
                filter:'brightness(0) saturate(100%) invert(20%)'}}
            />

            {/* Asset Collection */}
            <Link
              to={`/asset-collection/${project._id}`}
              style={{
                marginLeft: 6,
                padding: '6px 10px',
                background: '#555',
                borderRadius: 6,
                color: '#fff',
                textDecoration: 'none',
                fontWeight: 600,
                lineHeight: 1,
              }}
            >
              Asset Collection
            </Link>
          </div>

          {project.isArchived && (
            <span style={{
              display:'inline-block',background:'#333',padding:'4px 8px',
              borderRadius:5,color:'gray',marginBottom:10}}>Archived</span>
          )}

          {displayedArtists.length>0 && (
            <p style={{margin:'0 0 30px',fontSize:'1.1rem',fontWeight:'bold'}}>
              {displayedArtists.join(', ')}
            </p>
          )}

          {project.projectType && (
            <p style={{margin:'5px 0',color:'#ccc'}}>
              <strong>Project Type:</strong> {project.projectType}
            </p>
          )}

          {relDate && (
            <p style={{margin:'5px 0',color:'#ccc'}}>
              <strong>Release Date:</strong> {relDate}
            </p>
          )}

          {Number(project.budget)>0 && (
            <p style={{margin:'5px 0',color:'#ccc',display:'flex',alignItems:'center',gap:10}}>
              <span>
                <strong>Budget:</strong> €{totalBudget.toLocaleString()}
              </span>
              <button
                onClick={() => setShowReactiveBudgetModal(true)}
                style={{
                  padding: '4px 10px',
                  background: '#555',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                }}
                title="Add Reactive Budget"
              >
                + Add Budget
              </button>
            </p>
          )}

          {/* Budget Breakdown Section - only show if there are reactive budgets */}
          {project.reactiveBudgets && project.reactiveBudgets.length > 0 && (
            <div style={{ marginTop: 15, maxWidth: 500 }}>
              <div
                onClick={() => setBudgetBreakdownOpen(!budgetBreakdownOpen)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <span style={{ fontSize: 14 }}>
                  {budgetBreakdownOpen ? '▼' : '▶'}
                </span>
                <strong style={{ color: '#ddd', fontSize: 14 }}>Budget Breakdown</strong>
              </div>

              {budgetBreakdownOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
                  {/* Initial Budget */}
                  <div
                    style={{
                      background: '#2a2a2a',
                      padding: '10px 12px',
                      borderRadius: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ color: '#999', fontWeight: 600, fontSize: 16 }}>
                        €{initialBudget.toLocaleString()}
                      </div>
                      <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                        Initial Budget
                      </div>
                    </div>
                  </div>

                  {/* Reactive Budgets */}
                  {project.reactiveBudgets.map((budget) => (
                    <div
                      key={budget._id}
                      style={{
                        background: '#2a2a2a',
                        padding: '10px 12px',
                        borderRadius: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#7eef4f', fontWeight: 700, fontSize: 16 }}>
                          €{budget.amount.toLocaleString()}
                        </div>
                        {budget.note && (
                          <div style={{ color: '#999', fontSize: 12, marginTop: 4 }}>
                            {budget.note}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button
                          onClick={() => {
                            setEditingReactiveBudget(budget);
                            setShowReactiveBudgetModal(true);
                          }}
                          style={{
                            padding: '6px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Edit"
                        >
                          <img
                            src={pencilIcon}
                            alt="Edit"
                            style={{
                              width: 16,
                              height: 16,
                              filter: 'brightness(0) saturate(100%) invert(60%)',
                            }}
                          />
                        </button>
                        <button
                          onClick={() => handleDeleteReactiveBudget(budget._id)}
                          style={{
                            padding: '6px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title="Delete"
                        >
                          <img
                            src={trashIcon}
                            alt="Delete"
                            style={{
                              width: 16,
                              height: 16,
                              filter: 'brightness(0) saturate(100%) invert(40%) sepia(100%) saturate(500%) hue-rotate(330deg)',
                            }}
                          />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          
        </div>
      </div>

      {/* LINKED RELEASES */}
      <div style={{marginTop:40}}>
        <h2 style={{marginBottom:20}}>Linked Releases</h2>
        <div style={{display:'flex',flexWrap:'wrap',gap:20}}>
          {/* add card */}
          <div onClick={()=>setShowReleaseModal(true)} style={{
            width:150,height:150,display:'flex',justifyContent:'center',alignItems:'center',
            background:'#1e1e1e',borderRadius:10,cursor:'pointer',color:'#555'}}>
            <div style={{fontSize:'3rem'}}>+</div>
          </div>

          {linkedReleases.map(rel=>(
            <Link
              key={rel._id}
              to={`/release/${rel._id}`}
              state={{ from: 'project', projectId: project._id }}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <div style={{width:150,background:'#1e1e1e',padding:10,borderRadius:10}}>
                <img src={rel.coverArt||emptyArt} alt="" style={{
                  width:'100%',aspectRatio:'1/1',objectFit:'cover',borderRadius:5,marginBottom:10}}/>
                <p style={{margin:0,fontWeight:'bold',fontSize:'.9rem',
                          textOverflow:'ellipsis',whiteSpace:'nowrap',overflow:'hidden'}}>
                  {rel.title}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* TAB BAR */}
      <div style={{marginTop:40,borderBottom:'1px solid #333',display:'flex',gap:20}}>
        {[
          {key:'marketing',label:'Marketing Breakdown'},
          {key:'invoices', label:'Project Invoices'},
        ].map(t=>(
          <button key={t.key} onClick={()=>setActiveTab(t.key)} style={{
            background:'transparent',border:'none',cursor:'pointer',
            color:activeTab===t.key?'#fff':'#888',padding:'8px 0',
            fontSize:'1rem',borderBottom:activeTab===t.key
              ?'3px solid #28a745':'3px solid transparent'}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* TABS */}
      {activeTab==='marketing' && (
        <div style={{marginTop:30}}>
          <MarketingBreakdown
            projectId={project._id}
            releases={linkedReleases}
            budget={totalBudget}
            initialBudget={initialBudget}
            reactiveBudgetsTotal={reactiveBudgetsTotal}/>
        </div>
      )}
      {activeTab==='invoices' && (
        <div style={{marginTop:30}}>
          <ProjectInvoices
            projectId={project._id}
            invoices={allInvoices}
            releases={allReleases}
            handleInvoiceStatusChange={handleInvoiceStatusChange}
            budget={totalBudget}/>
        </div>
      )}

      {/* EDIT PROJECT modal (uses shared ProjectModal) */}
      <ProjectModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleUpdateProject}
        onDelete={() => {
          setIsEditModalOpen(false);
          setTimeout(() => setIsDeleteConfirmOpen(true), 0);
        }}
        initialData={initialForEdit}
        allArtists={allArtists}
        allProjects={allProjects}
      />

      {/* DELETE confirm modal */}
      {isDeleteConfirmOpen && (
        <div className="modal">
          <div className="modal-content" style={{
            width:400,padding:20,background:'#1e1e1e',color:'#fff',
            borderRadius:10,margin:'auto'}}>
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete this project?</p>
            <div style={{display:'flex',justifyContent:'space-between'}}>
              <button onClick={async()=>{
                try{
                  const res=await api(`/api/projects/${project._id}`,{method:'DELETE'});
                  if(!res.ok) throw new Error(`Delete failed (${res.status})`);
                  navigate('/project-overview');
                }catch(err){alert(err.message);}
              }} style={{
                padding:'10px 20px',background:'#ea5a54',border:'none',
                color:'#fff',borderRadius:5,cursor:'pointer'}}>Delete</button>
              <button onClick={()=>setIsDeleteConfirmOpen(false)} style={{
                padding:'10px 20px',background:'#555',border:'none',
                color:'#fff',borderRadius:5,cursor:'pointer'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ARCHIVE confirm modal */}
      {isArchiveConfirmOpen && (
        <div className="modal">
          <div className="modal-content" style={{
            width:400,padding:20,background:'#1e1e1e',color:'#fff',
            borderRadius:10,margin:'auto'}}>
            <h2>Confirm</h2>
            <p>Are you sure you want to {project.isArchived?'unarchive':'archive'} this project?</p>
            <div style={{display:'flex',justifyContent:'space-between'}}>
              <button onClick={async()=>{
                try{
                  const body={...project,isArchived:!project.isArchived};
                  const res=await api(`/api/projects/${project._id}`,{
                    method:'PUT',headers:{'Content-Type':'application/json'},
                    body:JSON.stringify(body)});
                  if(!res.ok) throw new Error(`Toggle failed (${res.status})`);
                  setProject(await res.json()); setIsArchiveConfirmOpen(false);
                }catch(err){alert(err.message);}
              }} style={{
                padding:'10px 20px',background:'#28a745',border:'none',
                color:'#fff',borderRadius:5,cursor:'pointer'}}>
                {project.isArchived?'Unarchive':'Archive'}
              </button>
              <button onClick={()=>setIsArchiveConfirmOpen(false)} style={{
                padding:'10px 20px',background:'#555',border:'none',
                color:'#fff',borderRadius:5,cursor:'pointer'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD RELEASE modal */}
 <ReleaseModal
  open={showReleaseModal}
  projectContextId={project._id}
  allArtists={allArtists}
  allProjects={[
    {
      _id: project._id,
      title: project.title || '',
      // pass artist IDs so ReleaseModal can auto-fill
      artists: Array.isArray(project.artists) ? project.artists.map(a => a._id) : [],
    },
    // keep the rest, but avoid duplicating the current project
    ...allProjects.filter(p => String(p._id) !== String(project._id)),
  ]}
  onArtistsRefresh={async () => {
    try {
      const r = await api('/api/artists');
      if (!r.ok) throw new Error('refresh artists failed');
      setAllArtists(await r.json());
    } catch (e) { /* ignore */ }
  }}
  onCancel={() => setShowReleaseModal(false)}
  onSave={async (draft) => {
    try {
      const res = await api('/api/releases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...draft, projectId: project._id }),
      });
      if (!res.ok) throw new Error(`Create release failed (${res.status})`);
      await fetchReleases(project._id);
      setShowReleaseModal(false);
    } catch (err) {
      alert(err.message);
    }
  }}
/>

      {/* REACTIVE BUDGET modal */}
      {showReactiveBudgetModal && (
        <div
          className="modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => {
            setShowReactiveBudgetModal(false);
            setEditingReactiveBudget(null);
            setReactiveBudgetAmount('');
            setReactiveBudgetNote('');
          }}
        >
          <div
            className="modal-content"
            style={{
              width: 450,
              padding: 24,
              background: '#1e1e1e',
              color: '#fff',
              borderRadius: 10,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingReactiveBudget ? 'Edit Reactive Budget' : 'Add Reactive Budget'}
            </h2>

            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              Amount (€)
            </label>
            <input
              type="number"
              value={reactiveBudgetAmount}
              onChange={(e) => setReactiveBudgetAmount(e.target.value)}
              placeholder="e.g. 5000"
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 16,
                boxSizing: 'border-box',
                fontSize: 14,
              }}
              autoFocus
            />

            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>
              Note (optional)
            </label>
            <textarea
              value={reactiveBudgetNote}
              onChange={(e) => setReactiveBudgetNote(e.target.value)}
              placeholder="e.g. Additional marketing budget for Q2"
              rows={3}
              style={{
                width: '100%',
                padding: 10,
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                marginBottom: 20,
                boxSizing: 'border-box',
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowReactiveBudgetModal(false);
                  setEditingReactiveBudget(null);
                  setReactiveBudgetAmount('');
                  setReactiveBudgetNote('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#555',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={editingReactiveBudget ? handleUpdateReactiveBudget : handleAddReactiveBudget}
                style={{
                  padding: '10px 20px',
                  background: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 5,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {editingReactiveBudget ? 'Update' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectDetails;